import { SortingState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import TableContainer from "Common/TableContainer";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import SearchBox from "Components/SearchBox/SearchBox";
import { app_modules } from "helpers/apps_modules_list";
import {
  Fragment,
  useEffect,
  useRef,
  useState
} from "react";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import {
  exportBrandCSV,
  fetchBrands as OnfetchBrand
} from "slices/brand/thunk";

const Brand = () => {
  document.title = "Brand List";
  const dispatch = useDispatch<any>();

  const selectBrand = createSelector(
    (state: any) => state.Brand,
    (brand) => ({
      Brands: brand.brandList,
      rowCount: brand.rowCount,
      loading: brand.loading,
      error: brand.error,
    })
  );

  const { Brands, rowCount, loading, error } = useSelector(selectBrand);

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.master?.app_name
  );
  const brand_permissions = master_permissions?.modules_permissions?.find(
    (item: any) =>
      item?.modules_name === app_modules?.master?.modules?.brand
  );

  //*// For redirecting back to this page and get the same paginated pageIndex
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page_brand") || "0");

  const [pageIndex, setPageIndex] = useState(pageParam);

  // Save current page to sessionStorage when component mounts
  useEffect(() => {
    if (pageParam > 0) {
      sessionStorage.setItem("brandPage", pageParam.toString());
    } else {
      // Clear sessionStorage when navigating from sidebar (page 0)
      sessionStorage.removeItem("brandPage");
    }
  }, [pageParam]);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(rowCount);

  // Sync totalRecords with rowCount from Redux state
  useEffect(() => {
    setTotalRecords(rowCount);
  }, [rowCount]);

  // State for edit functionality
  const [editUnit, setEditUnit] = useState<boolean>(false);
  const [columnFilters, setColumnFilters] = useState<any>();
  const [edit, setEdit] = useState<any>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] =
    useState<boolean>(false);

  useEffect(() => {
    dispatch(
      OnfetchBrand({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, sortField, sortDirection]);

  const prevFilters = useRef(filterValue);

  useEffect(() => {
    if (JSON.stringify(prevFilters.current) !== JSON.stringify(filterValue)) {
      prevFilters.current = filterValue;
      dispatch(
        OnfetchBrand({
          page: 1, // Always use page 1 when filters change
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    }
  }, [filterValue]);

  // Sync pageIndex with URL parameters
  useEffect(() => {
    const currentPageParam = parseInt(searchParams.get("page_brand") || "0");
    if (currentPageParam !== pageIndex) {
      setPageIndex(currentPageParam);
    }
  }, [searchParams, pageIndex]);

  //*// All pagination Issues(resolved) when redirecting from the edit or view page
  // Reset pagenumber to 0 when not first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handlePageChange(0);
  }, [sorting, pageSize, columnFilters, search]);
  const handlePageChange = (newPage: number) => {
    setPageIndex(newPage);
    setSearchParams({ page_brand: newPage.toString() });
    // Save current page to sessionStorage for back navigation
    sessionStorage.setItem("brandPage", newPage.toString());
  };
  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  //*//

  //*// Fetch Permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
      }
    };
    fetchPermissions();
  }, [dispatch]);

  // Handle edit action
  // const handleEdit = (item: any) => {
  //   setEditUnit(!editUnit);
  //   setEdit({
  //     id: item.id,
  //     brandName: item.brand_name,
  //     remark: item.remark,
  //     status: item.is_active,
  //     createdBy: item.created_by,
  //     updatedBy: item.updated_by,
  //     createDate: item.created_at,
  //     updatedDate: item.updated_at,
  //   });
  // };
  const handleStatusToggle = async (brand: any) => {
    try {
      const currentStatus = brand.status ?? brand.is_active;
      const updatedStatus = !currentStatus;
      const brandId = brand.brand_id || brand.id;

      const payload = { is_active: updatedStatus };

      setIsStatusUpdateLoading(true);

      await Axios.patch(
        `api/brand/update/${brandId}/`,
        payload
      );

      toast.success(
        `Brand ${updatedStatus ? "activated" : "deactivated"} successfully`
      );

      dispatch(
        OnfetchBrand({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    } catch (err: any) {
      toast.error("Failed to Update status !");
    } finally {
      setIsStatusUpdateLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  const handleExport = async () => {
    try {
      const { blob, filename } = await exportBrandCSV({
        search,
        filters: filterValue,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  const DynamicAction =
    brand_permissions?.permissions?.Update === true
      ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => {
            const brandData = row.original; // full row object

            return (
              <ul className="d-flex gap-2 list-unstyled mb-0">
                <li>
                  <Link
                    to={`/edit-brand/${brandData.brand_id || brandData.id}`}
                    state={{ brandData }}
                    className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn"
                  >
                    <i className="ph-pencil"></i>
                  </Link>
                </li>
              </ul>
            );
          },
        },
      ]
      : [];

  const columns = [
    {
      header: "Brand Name",
      accessorKey: "brand_name",
      enableSorting: true,
      enableColumnFilter: true,
      filterType: "dropdown" as const,
      filterFetchUrl:
        "api/brand/dropdown/?field=brand_name",
    },
    {
      header: "Remark",
      accessorKey: "remark",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }: { row: any }) => {
        const remark = row.original.remark;
        // return remark || "-";
        return (
        <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>
          {remark || "-"}
        </div>
      );
      },
    },
    {
      header: "Status",
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const brand = row.original;

        return (
          <div
            style={{ fontSize: "1.5rem", cursor: "pointer" }}
            onClick={() => handleStatusToggle(brand)}
          >
            <i
              className={`bi ${brand.status ?? brand.is_active
                  ? "bi-toggle-on text-primary"
                  : "bi-toggle-off text-danger"
                }`}
            />
          </div>
        );
      },
    },
    {
      header: "Created By/Date",
      accessorKey: "created_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => {
        const name = row.original.created_by ?? "N/A";
        const date = formatDate(row.original.created_at);
        return `  ${name}  -  ${date}  `;
      },
    },
    {
      header: "Updated By/Date",
      accessorKey: "updated_at",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => {
        const name = row.original.updated_by ?? "N/A";
        const date = formatDate(row.original.updated_at);
        return `  ${name}  -  ${date}  `;
      },
    },
    ...DynamicAction,
  ];

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="brandList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Brand</h6>
                    </Col>

                    {/* Search Input Box (reusable)*/}
                    <Col xl={2} md={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        <Button
                          className="btn btn-subtle-danger d-none"
                          id="remove-actions"
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>
                        {brand_permissions?.permissions?.View === true && (
                          <div
                            className="hstack gap-2 btn btn-light"
                            onClick={handleExport}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="bi bi-box-arrow-up align-baseline me-1"></i>{" "}
                            Export
                          </div>
                        )}
                        {
                          brand_permissions?.permissions?.Create === true &&
                          <Link
                            to="/add-brand"
                            className="btn"
                            style={{
                              backgroundColor: "#0073BB",
                              borderColor: "#0073BB",
                              color: "#fff",
                            }}
                          >
                            <i className="bi bi-plus-circle align-baseline me-1"></i>{" "}
                            Add Brand
                          </Link>
                        }
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={Brands || []}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      SearchPlaceholder="Search Brand..."
                      manualSorting={true}
                      colValue={setFilterValue}
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default Brand;

